/**
 * Level-authoring combinators. Levels are authored here in TypeScript for
 * ergonomics but produce exact `LevelFile` JSON values — the format contract
 * (Appendix A) is unchanged; `JSON.stringify(level)` is a valid community-
 * editor file (see DECISIONS D-017).
 *
 * Medals v2 (R2 §1.6, supersedes D-018): times are AUTHORED again — the
 * throttle makes finish time skill-driven. `author` = the recorded autopilot
 * best (`authorTimes.json`, regenerated by the authoring-ghost harness);
 * the ladder derives via MEDAL_LADDER multipliers.
 */
import { MEDAL_LADDER } from "../config/tuning";
import { SONGS } from "../data/songs";
import type { LevelFile, LevelPrompt, MedalTimes } from "../track/types";
import authorTimes from "./authorTimes.json";

export const F = "_______";
export const GAP = ".......";

export const rep = (row: string, n: number): string[] => Array.from({ length: n }, () => row);

/** Each obstacle row followed by `spacing − 1` floor rows. */
export const every = (spacing: number, ...obstacles: string[]): string[] =>
  obstacles.flatMap((o) => [o, ...rep(F, spacing - 1)]);

export interface LevelDef {
  id: string;
  name: string;
  songId: string;
  world: number;
  rows: string[];
  prompts?: LevelPrompt[];
  /** Skip medal computation (tutorial). */
  noMedals?: boolean;
  /** v2: gravity preset (R2 §1.3). */
  physics?: LevelFile["physics"];
  /** v2: validated minimum throttle (R2 §1.4). */
  minThrottle?: number;
}

/** Medal ladder from the authored (autopilot-recorded) best time. */
export function medalsFromAuthor(authorMs: number): MedalTimes {
  return {
    author: Math.round(authorMs),
    gold: Math.round(authorMs * MEDAL_LADDER.gold),
    silver: Math.round(authorMs * MEDAL_LADDER.silver),
    bronze: Math.round(authorMs * MEDAL_LADDER.bronze),
  };
}

export function buildLevel(def: LevelDef): LevelFile {
  if (!SONGS[def.songId]) throw new Error(`unknown song "${def.songId}"`);
  const v2 = def.physics !== undefined || def.minThrottle !== undefined;
  const file: LevelFile = {
    format: v2 ? "skypulse-level@2" : "skypulse-level@1",
    id: def.id,
    name: def.name,
    songId: def.songId,
    world: def.world,
    palette: `world${def.world}`,
    rows: def.rows,
  };
  if (def.physics) file.physics = def.physics;
  if (def.minThrottle !== undefined) file.minThrottle = def.minThrottle;
  if (!def.noMedals) {
    const authorMs = (authorTimes as Record<string, number>)[def.id];
    // Missing = ghost not yet recorded (GENERATE_GHOSTS=1 harness run) —
    // the level stays playable, medals simply don't show.
    if (authorMs !== undefined) file.medalTimesMs = medalsFromAuthor(authorMs);
  }
  if (def.prompts) file.prompts = def.prompts;
  return file;
}
